import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import useStoreSubdomain, { useStorePath } from '@/hooks/useStoreSubdomain';
import useStoreBranding from '@/hooks/useStoreBranding';
import api from '@/lib/api';
import { ArrowLeft, ChevronDown, ChevronUp, HelpCircle, Loader2 } from 'lucide-react';
import { DEFAULT_STORE_PRIMARY } from '@/lib/storeTheme';

export default function PublicFAQ() {
  const subdomain = useStoreSubdomain();
  const storePath = useStorePath();
  const { theme, loading: brandingLoading } = useStoreBranding();
  const [store, setStore] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIndex, setOpenIndex] = useState(null);

  const primaryColor = theme?.primary || store?.primary_color || DEFAULT_STORE_PRIMARY;

  useEffect(() => {
    const fetch = async () => {
      try {
        const [storeRes, faqRes] = await Promise.all([
          api.get(`/store/${subdomain}`),
          api.get(`/store/${subdomain}/faq`)
        ]);
        setStore(storeRes.data);
        if (storeRes.data.name) document.title = `FAQ — ${storeRes.data.name}`;
        setItems(faqRes.data.items || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, [subdomain]);

  if (loading || brandingLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 size={32} className="animate-spin" style={{ color: primaryColor }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to={storePath('/')} className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft size={20} /><span className="font-medium">{store?.name || theme?.brandName}</span>
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div
            className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center shadow-sm"
            style={{ backgroundColor: primaryColor }}
          >
            <HelpCircle size={32} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Pertanyaan Umum (FAQ)</h1>
          <p className="text-slate-500 mt-1">Temukan jawaban untuk pertanyaan yang sering ditanyakan</p>
        </div>
        {items.length === 0 ? (
          <p className="text-center text-slate-400 py-12">Belum ada FAQ</p>
        ) : (
          <div className="space-y-3">
            {items.map((item, i) => (
              <div key={i} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                <button
                  onClick={() => setOpenIndex(openIndex === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-50 transition-colors"
                >
                  <span className="font-medium text-slate-900">{item.question}</span>
                  {openIndex === i ? (
                    <ChevronUp size={18} className="text-slate-400 shrink-0" />
                  ) : (
                    <ChevronDown size={18} className="text-slate-400 shrink-0" />
                  )}
                </button>
                {openIndex === i && (
                  <div className="px-5 pb-4 text-sm text-slate-600 border-t border-slate-100 pt-3">
                    {item.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
